// src/pages/sis/Dashboard.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { getAllStudents as loadStudents, refreshStudentsFromFirestore } from "../../services/studentsService";
import { loadSectionAdvisers } from "../../services/sectionsService";
import { getCurrentUser } from "../../auth";
import { db } from "../../services/firebase";
import { collection, onSnapshot, query as fsQuery, where as fsWhere } from "firebase/firestore";
import { Chart } from "react-chartjs-2";
import "chart.js/auto";

export default function SISDashboard() {
  const currentYear = new Date().getFullYear();
  const currentUser = getCurrentUser();
  const role = String(currentUser?.role || "").toLowerCase();
  const userCampus = String(currentUser?.campus || "").toLowerCase();
  const isAdmin = role === "admin" || role === "superadmin" || userCampus === "all";
  const [students, setStudents] = useState(() => loadStudents());
  const [teachersCount, setTeachersCount] = useState(0);
  const [parentsCount, setParentsCount] = useState(0);
  const [earnings, setEarnings] = useState(0);
  const [payments, setPayments] = useState([]);
  const [accent, setAccent] = useState(() => {
    try {
      return localStorage.getItem("accentCampus") || (isAdmin ? "" : userCampus);
    } catch (e) { return isAdmin ? "" : userCampus; }
  });
  const [gradeFilter, setGradeFilter] = useState("");
  const [sectionFilter, setSectionFilter] = useState("");
  const [reportView, setReportView] = useState("students");
  const [serverPaging, setServerPaging] = useState(false);
  const [pageSize, setPageSize] = useState(50);
  const [pageRows, setPageRows] = useState([]);
  const [pageStack, setPageStack] = useState([]);
  const [pageEnd, setPageEnd] = useState(null);
  const normCampus = (v) => {
    const x = String(v || "").toLowerCase().trim();
    if (x.includes("gapan")) return "gapan";
    if (x.includes("bulacan")) return "bulacan";
    return x;
  };
  const splitSections = (obj) => {
    if (Array.isArray(obj)) return obj.map(s => String(s).trim()).filter(Boolean);
    if (obj && Array.isArray(obj.sections)) return obj.sections.map(s => String(s).trim()).filter(Boolean);
    return String(obj || "").split(",").map(s => s.trim()).filter(Boolean);
  };
  const tableRef = useRef(null);
  const [advisers, setAdvisers] = useState({});
  useEffect(() => {
    (async () => {
      const scope = (isAdmin && accent) ? accent : (isAdmin ? "" : userCampus);
      if (scope) {
        const adv = await loadSectionAdvisers(scope);
        setAdvisers(adv || {});
      } else {
        setAdvisers({});
      }
    })();
  }, [accent, isAdmin, userCampus]);
  const formatName = (s) => {
    const last = String(s.lastName || "").trim();
    const first = String(s.firstName || "").trim();
    const middle = String(s.middleName || s.middleInitial || "").trim();
    if (last || first) {
      const core = `${last}${last && first ? ", " : ""}${first}`;
      return middle ? `${core} ${middle}` : core;
    }
    return String(s.fullname || "").trim();
  };
  function handlePrint() {
    const campusName = (isAdmin && !accent) ? "All Campuses" : (accent || userCampus);
    const campusFiltered = (isAdmin && !accent) ? students : students.filter(s => String(s.campus || "").toLowerCase() === (accent || userCampus));
    const byGrade = gradeFilter ? campusFiltered.filter(s => String(s.gradeLevel || "") === gradeFilter) : campusFiltered;
    const bySection = sectionFilter ? byGrade.filter(s => splitSections(s.section).includes(sectionFilter)) : byGrade;
    const count = bySection.length;
    const html = `
      <html>
        <head>
          <meta charset="utf-8"/>
          <title>Students List</title>
          <style>
            @page { size: letter portrait; margin: 12mm; }
            body { font-family: -apple-system, Segoe UI, Roboto, Arial, sans-serif; color: #000; }
            h3 { margin: 0 0 8px 0; }
            .meta { margin-bottom: 8px; font-size: 12px; }
            table { width: 100%; border-collapse: collapse; font-size: 12px; }
            thead { display: table-header-group; }
            th, td { border: 1px solid #ccc; padding: 6px 8px; text-align: left; }
          </style>
        </head>
        <body>
          <h3>Students List</h3>
          <div class="meta">
            <div><strong>Campus:</strong> ${String(campusName).toUpperCase()}</div>
            <div><strong>Filter:</strong> ${gradeFilter || "All Grades"}${sectionFilter ? ` • ${sectionFilter}` : ""}</div>
            <div><strong>Students:</strong> ${count}</div>
            <div><strong>Date:</strong> ${new Date().toLocaleDateString()}</div>
          </div>
          ${tableRef.current ? tableRef.current.innerHTML : ""}
        </body>
      </html>`;
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.open();
    w.document.write(html);
    w.document.close();
    w.onload = () => {
      try {
        w.focus();
        w.print();
        w.close();
      } catch (e) {
        // fallback small delay for some browsers
        setTimeout(() => { try { w.focus(); w.print(); w.close(); } catch (_) { void 0; } }, 300);
      }
    };
  }
  useEffect(() => {
    (async () => {
      try {
        const scopeCampus = (isAdmin && accent) ? accent : (isAdmin ? "" : userCampus);
        const list = await refreshStudentsFromFirestore({ campus: scopeCampus, isAdmin });
        if (Array.isArray(list) && list.length) setStudents(list);
      } catch (e) { /* ignore */ }
    })();
    const scopeCampus = (isAdmin && accent) ? accent : (isAdmin ? "" : userCampus);
    const baseCol = collection(db, "students");
    const colRef = baseCol; // listen to all, filter in-memory for reliability across campus strings
    const unsub = onSnapshot(
      colRef,
      (snap) => {
        const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        try { localStorage.setItem("students_fs_cache", JSON.stringify(list)); } catch (e) { /* ignore */ }
        setStudents(list);
      },
      () => { /* ignore */ }
    );
    const unsubEmp = onSnapshot(collection(db, "employees"), (snap) => {
      const rows = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      const filterCampus = (isAdmin && !accent) ? (x => true) : (x => {
        const c = String(x.campus || x.Campus || x.campusCode || x.location || "").toLowerCase();
        return c === (accent || userCampus);
      });
      const isTeacherRow = (e) => {
        const text = [
          e.position, e.department, e.role, e.designation, e.title, e.jobTitle, e.job_title, e.category, e.unit
        ].map(v => String(v || "").toLowerCase()).join(" ");
        return e.isTeacher === true || String(e.type || "").toLowerCase() === "teacher" || text.includes("teacher");
      };
      setTeachersCount(rows.filter(e => filterCampus(e) && isTeacherRow(e)).length);
    }, () => { /* ignore */ });
    const selCampus = (accent || userCampus);
    const payCol = collection(db, "payments");
    const payQ = selCampus ? fsQuery(payCol, fsWhere("campus","==", selCampus)) : payCol;
    const unsubPay = onSnapshot(payQ, (snap) => {
      const rows = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setPayments(rows);
    }, () => { setPayments([]); });
    return () => { unsub(); unsubEmp(); unsubPay(); };
  }, []);

  useEffect(() => {
    // Recompute earnings whenever payments, campus accent, or students change
    const normCampus = (v) => {
      const x = String(v || "").toLowerCase().trim();
      if (x.includes("gapan")) return "gapan";
      if (x.includes("bulacan")) return "bulacan";
      return x;
    };
    const sums = payments.filter(p => {
      const pc = normCampus(p.campus);
      if (isAdmin && !accent) return true;
      if (pc) return pc === (accent || userCampus);
      const s = students.find(x => String(x.id) === String(p.studentId || p.student_id || ""));
      return s && normCampus(s.campus) === (accent || userCampus);
    }).reduce((s, p) => s + Number(p.amount || 0), 0);
    setEarnings(sums);
  }, [payments, students, accent, userCampus, isAdmin]);

  useEffect(() => {
    // Immediate client-side filter for table responsiveness
    const selectedCampus = (accent || userCampus);
    const campusRows = (isAdmin && !accent)
      ? students
      : students.filter(s => normCampus(s.campus) === selectedCampus);
    const gradeRows = gradeFilter ? campusRows.filter(s => String(s.gradeLevel || "") === gradeFilter) : campusRows;
    const sectionRows = sectionFilter ? gradeRows.filter(s => splitSections(s.section || s.sections || []).includes(sectionFilter)) : gradeRows;
    setPageRows(sectionRows.slice(0, pageSize));
    setPageEnd(null);
    setPageStack([]);
  }, [students, gradeFilter, sectionFilter, accent, userCampus, isAdmin, pageSize]);

  useEffect(() => {
    const selectedCampus = (accent || userCampus);
    const campusRows = (isAdmin && !accent)
      ? students
      : students.filter(s => normCampus(s.campus) === selectedCampus);
    const validGrades = new Set(campusRows.map(s => String(s.gradeLevel || "")).filter(Boolean));
    const gradeRows = gradeFilter ? campusRows.filter(s => String(s.gradeLevel || "") === gradeFilter) : campusRows;
    const validSections = new Set(gradeRows.flatMap(s => splitSections(s.section || s.sections || [])));

    if (gradeFilter && !validGrades.has(gradeFilter)) {
      setGradeFilter("");
      setSectionFilter("");
      return;
    }
    if (sectionFilter && !validSections.has(sectionFilter)) {
      setSectionFilter("");
    }
  }, [students, gradeFilter, sectionFilter, accent, userCampus, isAdmin]);

  async function loadPage(direction) {
    const scopeCampus = (isAdmin && accent) ? accent : (isAdmin ? "" : userCampus);
    let base = collection(db, "students");
    let q = scopeCampus ? fsQuery(base, fsWhere("campus", "==", scopeCampus)) : base;
    if (gradeFilter) q = fsQuery(q, fsWhere("gradeLevel", "==", gradeFilter));
    const { orderBy, startAfter, limit } = await import("firebase/firestore");
    let constraints = [orderBy("lastNameLower"), orderBy("firstNameLower"), limit(pageSize)];
    if (direction === "next" && pageEnd) constraints = [orderBy("lastNameLower"), orderBy("firstNameLower"), startAfter(pageEnd), limit(pageSize)];
    const { getDocs, query: QQ } = await import("firebase/firestore");
    try {
      const snapshot = await getDocs(QQ(q, ...constraints));
      let rows = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      if (sectionFilter) {
        rows = rows.filter(s => splitSections(s.section || s.sections || []).includes(sectionFilter));
      }
      setPageRows(rows);
      if (direction === "next") setPageStack(st => st.concat([pageEnd]));
      if (rows.length > 0) setPageEnd(snapshot.docs[snapshot.docs.length - 1]);
    } catch (e) {
      // Fallback to document id ordering if composite index not yet available
      let fallback = [orderBy("__name__"), limit(pageSize)];
      if (direction === "next" && pageEnd) fallback = [orderBy("__name__"), startAfter(pageEnd), limit(pageSize)];
      const snapshot = await getDocs(QQ(q, ...fallback));
      let rows = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      if (sectionFilter) {
        rows = rows.filter(s => splitSections(s.section || s.sections || []).includes(sectionFilter));
      }
      setPageRows(rows);
      if (direction === "next") setPageStack(st => st.concat([pageEnd]));
      if (rows.length > 0) setPageEnd(snapshot.docs[snapshot.docs.length - 1]);
    }
  }
  useEffect(() => {
    try {
      const gf = localStorage.getItem("sis_gradeFilter") || "";
      const sf = localStorage.getItem("sis_sectionFilter") || "";
      if (gf) setGradeFilter(gf);
      if (sf) setSectionFilter(sf);
    } catch (e) { void e; }
  }, []);
  useEffect(() => {
    try {
      localStorage.setItem("sis_gradeFilter", gradeFilter || "");
      localStorage.setItem("sis_sectionFilter", sectionFilter || "");
    } catch (e) { void e; }
    setPageStack([]);
    setPageEnd(null);
    if (serverPaging) loadPage("reset");
  }, [gradeFilter, sectionFilter, accent, userCampus, isAdmin, serverPaging, pageSize]);

  const stats = useMemo(() => {
    const selected = (accent || userCampus);
    const campusFiltered = (isAdmin && !accent)
      ? students
      : students.filter(s => normCampus(s.campus) === selected);
    const total = campusFiltered.length;
    const shs = campusFiltered.filter(s => /grade\s?1[1-2]/i.test(s.gradeLevel || "")).length;
    const campuses = Array.from(new Set(campusFiltered.map(s => normCampus(s.campus)))).filter(Boolean);
    const enrolledThisYear = campusFiltered.filter(s => Number(s.enrollmentYear) === currentYear).length;
    const parents = new Set(campusFiltered.map(s => String(s.guardianEmail || s.parentEmail || "").toLowerCase()).filter(Boolean)).size;

    const byCampus = campuses.map(c => ({
      campus: c,
      count: campusFiltered.filter(s => normCampus(s.campus) === c).length
    }));

    return { total, shs, campuses, enrolledThisYear, byCampus, parents };
  }, [currentYear, students, isAdmin, userCampus, accent]);

  return (
    <div className="container-fluid">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3 className="mb-0">Student Information System Dashboard</h3>
        <div className="d-flex align-items-center gap-2">
          <label className="form-label mb-0 small">Header Accent</label>
          <select
            className="form-select form-select-sm"
            value={accent}
            onChange={e => {
              const val = e.target.value;
              setAccent(val);
              try { localStorage.setItem("accentCampus", val || ""); } catch (e) { /* ignore */ }
              // header reacts on next paint
            }}
          >
            {isAdmin && <option value="">All</option>}
            <option value="gapan">Gapan</option>
            <option value="bulacan">Bulacan</option>
          </select>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="row g-3">
        <div className="col-md-4">
          <div className="card summary-card slim h-100 primary">
            <div className="card-body">
              <div className="summary-label"><i className="bi bi-people me-2 text-warning"></i>Total Students</div>
              <div className="summary-value">{stats.total}</div>
              <div className="small mt-2">Campuses: {stats.campuses.length}</div>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card summary-card slim h-100 success">
            <div className="card-body">
              <div className="summary-label"><i className="bi bi-calendar-check me-2 text-success"></i>Enrolled This Year</div>
              <div className="summary-value">{stats.enrolledThisYear}</div>
              <div className="small mt-2">School Year: {currentYear}</div>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card summary-card slim h-100 info">
            <div className="card-body">
              <div className="summary-label"><i className="bi bi-mortarboard me-2 text-primary"></i>SHS Students (Grade 11–12)</div>
              <div className="summary-value">{stats.shs}</div>
              <div className="small mt-2">Across {stats.campuses.length} campuses</div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card summary-card slim h-100 info">
            <div className="card-body">
              <div className="summary-label"><i className="bi bi-person-badge me-2 text-info"></i>Teachers</div>
              <div className="summary-value">{teachersCount}</div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card summary-card slim h-100 secondary">
            <div className="card-body">
              <div className="summary-label"><i className="bi bi-people-fill me-2 text-secondary"></i>Parents</div>
              <div className="summary-value">{stats.parents}</div>
            </div>
          </div>
        </div>
        <div className="col-md-6">
          <div className="card summary-card slim h-100 success">
            <div className="card-body">
              <div className="summary-label"><i className="bi bi-currency-dollar me-2 text-warning"></i>Total Earnings</div>
              <div className="summary-value">₱{Number(earnings).toLocaleString()}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="card mt-4">
        <div className="card-body print-students">
          <div className="row g-3 mb-3">
            <div className="col-md-3">
              <label className="form-label">Grade Level</label>
              <select
                className="form-select"
                value={gradeFilter}
                onChange={e => { setGradeFilter(e.target.value); setSectionFilter(""); }}
              >
                <option value="">All</option>
                {(() => {
                  const campusFiltered = (isAdmin && !accent) ? students : students.filter(s => String(s.campus || "").toLowerCase() === (accent || userCampus));
                  const grades = Array.from(new Set(campusFiltered.map(s => String(s.gradeLevel || "")))).filter(Boolean);
                  return grades.map(g => <option key={g} value={g}>{g}</option>);
                })()}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">Section</label>
              <select
                className="form-select"
                value={sectionFilter}
                onChange={e => setSectionFilter(e.target.value)}
              >
                <option value="">All</option>
                {(() => {
                  const campusFiltered = (isAdmin && !accent) ? students : students.filter(s => String(s.campus || "").toLowerCase() === (accent || userCampus));
                  const filteredByGrade = gradeFilter ? campusFiltered.filter(s => String(s.gradeLevel || "") === gradeFilter) : campusFiltered;
                  const sections = Array.from(new Set(filteredByGrade.flatMap(s => splitSections(s.section)))).filter(Boolean);
                  return sections.map(sec => <option key={sec} value={sec}>{sec}</option>);
                })()}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">View</label>
              <select className="form-select" value={reportView} onChange={e => setReportView(e.target.value)}>
                <option value="students">Students</option>
                <option value="contacts">Contacts (Guardian)</option>
                <option value="lrn">LRN List</option>
                <option value="noEmail">No Student Email</option>
                <option value="shsStrand">SHS Strand</option>
              </select>
            </div>
            <div className="col-md-3 d-flex align-items-end justify-content-end">
              <div className="d-flex gap-2">
                <button className="btn btn-outline-secondary no-print" onClick={() => {
                  const dataSource = serverPaging ? pageRows : ((isAdmin && !accent) ? students : students.filter(s => String(s.campus || "").toLowerCase() === (accent || userCampus)));
                  const byGrade = gradeFilter ? dataSource.filter(s => String(s.gradeLevel || "") === gradeFilter) : dataSource;
                  let rows = sectionFilter ? byGrade.filter(s => splitSections(s.section).includes(sectionFilter)) : byGrade;
                  if (reportView === "noEmail") rows = rows.filter(s => !(s.studentEmail || s.email));
                  if (reportView === "shsStrand") rows = rows.filter(s => /grade\s?1[1-2]/i.test(String(s.gradeLevel || "")));
                  const headersMap = {
                    students: ["Full Name","Gender","Grade","Section","Student Email"],
                    contacts: ["Full Name","Grade","Section","Guardian","Contact","Email"],
                    lrn: ["Full Name","Grade","Section","LRN"],
                    noEmail: ["Full Name","Grade","Section"],
                    shsStrand: ["Full Name","Grade","Section","Strand"]
                  };
                  const headers = headersMap[reportView] || headersMap.students;
                  const csvRows = rows.map(s => {
                    const sec = splitSections(s.section).join(" / ");
                    if (reportView === "contacts") {
                      const phone = s.guardianContact || s.parentContact || s.fatherContact || s.motherContact || "";
                      const email = s.guardianEmail || s.parentEmail || s.fatherEmail || s.motherEmail || "";
                      return [formatName(s), s.gradeLevel || "", sec, s.guardianName || s.parentName || "", phone, email];
                    }
                    if (reportView === "lrn") return [formatName(s), s.gradeLevel || "", sec, s.lrn || ""];
                    if (reportView === "noEmail") return [formatName(s), s.gradeLevel || "", sec];
                    if (reportView === "shsStrand") return [formatName(s), s.gradeLevel || "", sec, s.strand || ""];
                    return [formatName(s), s.gender || "", s.gradeLevel || "", sec, s.studentEmail || s.email || ""];
                  });
                  const csv = [headers.join(","), ...csvRows.map(r => r.map(v => `"${String(v||"").replace(/"/g,'""')}"`).join(","))].join("\n");
                  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `sis_${reportView}_${new Date().toISOString().slice(0,10)}.csv`;
                  a.click();
                  URL.revokeObjectURL(url);
                }}>
                  Export CSV
                </button>
                <button className="btn btn-primary no-print" onClick={handlePrint}>Print List</button>
              </div>
            </div>
          </div>
          {reportView === "shsStrand" && (() => {
            const dataSource = serverPaging ? pageRows : ((isAdmin && !accent) ? students : students.filter(s => String(s.campus || "").toLowerCase() === (accent || userCampus)));
            const byGrade = gradeFilter ? dataSource.filter(s => String(s.gradeLevel || "") === gradeFilter) : dataSource;
            const rows = (sectionFilter ? byGrade.filter(s => splitSections(s.section).includes(sectionFilter)) : byGrade)
              .filter(s => /grade\s?1[1-2]/i.test(String(s.gradeLevel || "")));
            const tally = {};
            rows.forEach(s => { const k = String(s.strand || "N/A"); tally[k] = (tally[k] || 0) + 1; });
            const entries = Object.entries(tally);
            return entries.length ? (
              <div className="alert alert-info py-2">
                <strong>SHS Strand Summary:</strong> {entries.map(([k,v]) => `${k}: ${v}`).join(" • ")}
              </div>
            ) : null;
          })()}
          <div className="d-flex align-items-center justify-content-end gap-2 mb-2">
            <div className="form-check">
              <input className="form-check-input" type="checkbox" id="serverPaging" checked={serverPaging} onChange={e => setServerPaging(e.target.checked)} />
              <label className="form-check-label" htmlFor="serverPaging">Server paging</label>
            </div>
            {serverPaging && (
              <>
                <select className="form-select" style={{ width: 90 }} value={pageSize} onChange={e => setPageSize(Number(e.target.value) || 50)}>
                  <option value="25">25</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
                <button className="btn btn-outline-secondary btn-sm" onClick={() => {
                  const prev = pageStack[pageStack.length - 2] || null;
                  setPageStack(st => st.slice(0, Math.max(0, st.length - 1)));
                  setPageEnd(prev); loadPage("prev");
                }} disabled={pageStack.length === 0}>Prev</button>
                <button className="btn btn-outline-secondary btn-sm" onClick={() => loadPage("next")}>Next</button>
              </>
            )}
          </div>
          <div className="table-responsive" ref={tableRef}>
            {/* Print header (shows only in print) */}
            <div className="print-only mb-2">
              {(() => {
                const campusName = (isAdmin && !accent) ? "All Campuses" : (accent || userCampus);
                const campusFiltered = (isAdmin && !accent) ? students : students.filter(s => String(s.campus || "").toLowerCase() === (accent || userCampus));
                const byGrade = gradeFilter ? campusFiltered.filter(s => String(s.gradeLevel || "") === gradeFilter) : campusFiltered;
                const bySection = sectionFilter ? byGrade.filter(s => splitSections(s.section).includes(sectionFilter)) : byGrade;
                const count = bySection.length;
                let adviser = "";
                if (gradeFilter && sectionFilter && advisers?.[gradeFilter]?.[sectionFilter]) adviser = advisers[gradeFilter][sectionFilter];
                return (
                  <div>
                    <div><strong>Campus:</strong> {String(campusName).toUpperCase()}</div>
                    <div><strong>Filter:</strong> {gradeFilter || "All Grades"}{sectionFilter ? ` • ${sectionFilter}` : ""}</div>
                    {adviser ? <div><strong>Adviser:</strong> {adviser}</div> : null}
                    <div><strong>Students:</strong> {count}</div>
                    <div><strong>Date:</strong> {new Date().toLocaleDateString()}</div>
                  </div>
                );
              })()}
            </div>
            <table className="table table-hover">
              <thead className="table-light">
                <tr>
                  {reportView === "contacts" && (<>
                    <th>Full Name</th><th>Grade</th><th>Section</th><th>Guardian</th><th>Contact</th><th>Email</th>
                  </>)}
                  {reportView === "lrn" && (<>
                    <th>Full Name</th><th>Grade</th><th>Section</th><th>LRN</th>
                  </>)}
                  {reportView === "noEmail" && (<>
                    <th>Full Name</th><th>Grade</th><th>Section</th>
                  </>)}
                  {reportView === "shsStrand" && (<>
                    <th>Full Name</th><th>Grade</th><th>Section</th><th>Strand</th>
                  </>)}
                  {reportView === "students" && (<>
                    <th>Full Name</th><th>Gender</th><th>Grade Level</th><th>Section</th><th>Student Email</th>
                  </>)}
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const dataSource = serverPaging ? pageRows : ((isAdmin && !accent) ? students : students.filter(s => String(s.campus || "").toLowerCase() === (accent || userCampus)));
                  const byGrade = gradeFilter ? dataSource.filter(s => String(s.gradeLevel || "") === gradeFilter) : dataSource;
                  let bySection = sectionFilter ? byGrade.filter(s => splitSections(s.section).includes(sectionFilter)) : byGrade;
                  if (reportView === "noEmail") bySection = bySection.filter(s => !(s.studentEmail || s.email));
                  if (reportView === "shsStrand") bySection = bySection.filter(s => /grade\s?1[1-2]/i.test(String(s.gradeLevel || "")));
                  const genderRank = (g) => {
                    const s = String(g || "").toLowerCase();
                    if (s.startsWith("m")) return 0; // male first
                    if (s.startsWith("f")) return 1; // female next
                    return 2; // unknown last
                  };
                  const sorted = bySection.slice().sort((a, b) => {
                    const ga = genderRank(a.gender), gb = genderRank(b.gender);
                    if (ga !== gb) return ga - gb;
                    return formatName(a).localeCompare(formatName(b));
                  });
                  return sorted.map(s => (
                    <tr key={s.id}>
                      {reportView === "contacts" && (<>
                        <td>{formatName(s)}</td>
                        <td>{s.gradeLevel || ""}</td>
                        <td>{splitSections(s.section).join(", ")}</td>
                        <td>{s.guardianName || s.parentName || ""}</td>
                        <td>{s.guardianContact || s.parentContact || s.fatherContact || s.motherContact || ""}</td>
                        <td>{s.guardianEmail || s.parentEmail || s.fatherEmail || s.motherEmail || ""}</td>
                      </>)}
                      {reportView === "lrn" && (<>
                        <td>{formatName(s)}</td>
                        <td>{s.gradeLevel || ""}</td>
                        <td>{splitSections(s.section).join(", ")}</td>
                        <td>{s.lrn || ""}</td>
                      </>)}
                      {reportView === "noEmail" && (<>
                        <td>{formatName(s)}</td>
                        <td>{s.gradeLevel || ""}</td>
                        <td>{splitSections(s.section).join(", ")}</td>
                      </>)}
                      {reportView === "shsStrand" && (<>
                        <td>{formatName(s)}</td>
                        <td>{s.gradeLevel || ""}</td>
                        <td>{splitSections(s.section).join(", ")}</td>
                        <td>{s.strand || ""}</td>
                      </>)}
                      {reportView === "students" && (<>
                        <td>{formatName(s)}</td>
                        <td className="text-capitalize">{s.gender || ""}</td>
                        <td>{s.gradeLevel || ""}</td>
                        <td>{splitSections(s.section).join(", ")}</td>
                        <td>{s.studentEmail || s.email || ""}</td>
                      </>)}
                    </tr>
                  ));
                })()}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="row mt-3">
        <div className="col-md-4">
          <div className="card h-100">
            <div className="card-body">
              {(() => {
                const campusFiltered = (isAdmin && !accent) ? students : students.filter(s => String(s.campus || "").toLowerCase() === (accent || userCampus));
                const order = ["Nursery","Kinder","Grade 1","Grade 2","Grade 3","Grade 4","Grade 5","Grade 6","Grade 7","Grade 8","Grade 9","Grade 10","Grade 11","Grade 12"];
                const counts = order.map(g => campusFiltered.filter(s => String(s.gradeLevel || "").toLowerCase() === String(g).toLowerCase()).length);
                const chartData = {
                  labels: order,
                  datasets: [{
                    label: "Students by Grade Level",
                    data: counts,
                    backgroundColor: "#ffca28",
                    borderColor: "#b2690f",
                  }]
                };
                const options = { responsive: true, plugins: { legend: { display: false } }, scales: { x: { ticks: { autoSkip: false } } } };
                return <Chart type="bar" data={chartData} options={options} />;
              })()}
            </div>
          </div>
        </div>
        <div className="col-md-8">
          <div className="card h-100">
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Grade Level</th>
                      <th>Section</th>
                      <th className="text-end">Students</th>
                      <th className="text-end">Total Payment</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const campusFiltered = (isAdmin && !accent) ? students : students.filter(s => String(s.campus || "").toLowerCase() === (accent || userCampus));
                      const ordered = campusFiltered.slice().sort((a,b) => String(a.gradeLevel||"").localeCompare(String(b.gradeLevel||"")) || String(String(a.section||"")).localeCompare(String(b.section||"")));
                      const paymentsFiltered = payments.filter(p => {
                        const pc = String(p.campus || "").toLowerCase();
                        if (isAdmin && !accent) return true;
                        if (pc) return pc === (accent || userCampus);
                        const s = campusFiltered.find(x => String(x.id) === String(p.studentId || p.student_id || ""));
                        return !!s;
                      });
                      const payMap = new Map();
                      paymentsFiltered.forEach(p => {
                        const sid = String(p.studentId || p.student_id || "");
                        payMap.set(sid, (payMap.get(sid) || 0) + Number(p.amount || 0));
                      });
                      const groupMap = new Map();
                      ordered.forEach(s => {
                        const secs = splitSections(s.section);
                        if (secs.length === 0) {
                          const key = `${String(s.gradeLevel||"")}|`;
                          const prev = groupMap.get(key) || { grade: s.gradeLevel||"", section: "", count: 0, total: 0 };
                          prev.count += 1;
                          prev.total += Number(payMap.get(String(s.id)) || 0);
                          groupMap.set(key, prev);
                        } else {
                          secs.forEach(sec => {
                            const key = `${String(s.gradeLevel||"")}|${sec}`;
                            const prev = groupMap.get(key) || { grade: s.gradeLevel||"", section: sec, count: 0, total: 0 };
                            prev.count += 1;
                            prev.total += Number(payMap.get(String(s.id)) || 0);
                            groupMap.set(key, prev);
                          });
                        }
                      });
                      const rows = Array.from(groupMap.values());
                      const totalStudents = rows.reduce((sum,r)=>sum+r.count,0);
                      const totalPayments = rows.reduce((sum,r)=>sum+r.total,0);
                      return (
                        <>
                          {rows.map((r,i)=>(
                            <tr key={`${r.grade}|${r.section}|${i}`}>
                              <td>{r.grade}</td>
                              <td>{r.section}</td>
                              <td className="text-end">{r.count}</td>
                              <td className="text-end">₱{Number(r.total).toLocaleString()}</td>
                            </tr>
                          ))}
                          <tr className="table-light">
                            <td colSpan="2" className="fw-bold">Total</td>
                            <td className="text-end fw-bold">{totalStudents}</td>
                            <td className="text-end fw-bold">₱{Number(totalPayments).toLocaleString()}</td>
                          </tr>
                        </>
                      );
                    })()}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Breakdown by Campus */}
      <div className="card shadow-sm border-0 mt-4">
        <div className="card-header bg-light">
          <h5 className="mb-0">Students by Campus</h5>
        </div>
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>Campus</th>
                  <th className="text-end">Students</th>
                </tr>
              </thead>
              <tbody>
                {stats.byCampus.map((row) => (
                  <tr key={row.campus}>
                    <td className="text-capitalize">{row.campus}</td>
                    <td className="text-end fw-semibold">{row.count}</td>
                  </tr>
                ))}
                {stats.byCampus.length === 0 && (
                  <tr>
                    <td colSpan="2" className="text-center text-muted">No campus data</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Quick Links */}
      <div className="row mt-4">
        <div className="col-md-4">
          <div className="card h-100 shadow-sm">
            <div className="card-body">
              <h6 className="fw-bold">Students</h6>
              <p className="text-muted mb-2">Browse and manage student records</p>
              <a className="btn btn-outline-primary" href="/sis/students">Open Students</a>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card h-100 shadow-sm">
            <div className="card-body">
              <h6 className="fw-bold">Enrollment</h6>
              <p className="text-muted mb-2">Enroll students for current school year</p>
              <a className="btn btn-outline-success" href="/sis/enrollment">Go to Enrollment</a>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card h-100 shadow-sm">
            <div className="card-body">
              <h6 className="fw-bold">Finance</h6>
              <p className="text-muted mb-2">View balances and payment history</p>
              <a className="btn btn-outline-warning" href="/sis/finance">Open Finance</a>
            </div>
          </div>
        </div>
      </div>
      <style>{`
        @media print {
          @page { size: letter portrait; margin: 12mm; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; background: #fff !important; color: #000 !important; }
          .no-print, .summary-card, .row.mt-4,
          .topbar, .sidebar, .vms-school-header, nav, header, footer { display: none !important; }
          .print-students { display: block !important; }
          .print-students .table { font-size: 12px; }
          .print-only { display: block !important; }
          thead { display: table-header-group; }
          .container-fluid, .print-students { padding: 0 !important; margin: 0 !important; }
        }
        .print-only { display: none; }
      `}</style>
    </div>
  );
}
